import React, { useState, useEffect, useRef } from 'react';

const PriceAlertPopup = ({ isOpen, onClose }) => {
  const [tickerCode, setTickerCode] = useState('');
  const [priceParam, setPriceParam] = useState('close');
  const [condition, setCondition] = useState('>=');
  const [targetPrice, setTargetPrice] = useState('');
  const [alertType, setAlertType] = useState('only');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [alertSuccess, setAlertSuccess] = useState(false);
  
  const suggestionsRef = useRef(null);
  
  // Price parameters options
  const priceParams = [
    { value: 'open', label: 'Open' },
    { value: 'high', label: 'High' },
    { value: 'low', label: 'Low' },
    { value: 'close', label: 'Close (Last price)' }
  ];
  
  const conditions = ['>=', '<='];

  // Function to clear all fields
  const clearAllFields = () => {
    setTickerCode('');
    setPriceParam('close');
    setCondition('>=');
    setTargetPrice('');
    setAlertType('only');
    setNotes('');
    setApiResponse(null);
  };
  
  // Function to fetch ticker symbol suggestions
  const fetchSymbolSuggestions = async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(`https://flask.finstrats.com/search?key=${encodeURIComponent(query)}`);
      if (!response.ok) {
        throw new Error('Failed to fetch suggestions');
      }
      const data = await response.json();
      setSuggestions(data);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setSuggestions([]);
    }
  };
  
  // Handle ticker code input changes with debounce
  const handleTickerInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setTickerCode(value);
    
    // Debounce the API call
    const timer = setTimeout(() => {
      fetchSymbolSuggestions(value);
    }, 300);
    
    return () => clearTimeout(timer);
  };
  
  // Handle clicking outside suggestions dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    setTickerCode(suggestion.ticker_code);
    setShowSuggestions(false);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!tickerCode || !targetPrice) {
      setApiResponse({ success: false, message: 'Please fill in all required fields' });
      return;
    }
    
    setIsLoading(true);
    setApiResponse(null);
    setAlertSuccess(false);
    
    const alertData = {
      ticker_code: tickerCode,
      condition_name: priceParam,
      condition_is: condition,
      target_price: parseFloat(targetPrice),
      notes: notes,
    };
    
    try {
      const response = await fetch('https://live1.finstrats.com/alert_stock', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(alertData)
      });
      
      const data = await response.json();
      setApiResponse(data);
      
      if (response.ok) {
        // Save ticker code before clearing for success message
        const savedTickerCode = tickerCode;
        
        // Clear all fields on success
        clearAllFields();
        
        setAlertSuccess(true);
        setTimeout(() => {
          setAlertSuccess(false);
          onClose();
        }, 2000);
      }
    } catch (error) {
      setApiResponse({ success: false, message: 'Network error: ' + error.message });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Styles
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: isOpen ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };
  
  const popupStyle = {
    backgroundColor: 'white',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    width: '100%',
    maxWidth: '500px',
    overflow: 'hidden'
  };
  
  const headerStyle = {
    padding: '16px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f8f9fa'
  };
  
  const titleStyle = {
    fontWeight: 'bold',
    color: '#333',
    display: 'flex',
    alignItems: 'center'
  };
  
  const contentStyle = {
    padding: '20px'
  };
  
  const rowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: '16px',
    alignItems: 'center'
  };
  
  const labelStyle = {
    width: '100px',
    color: '#555',
    fontWeight: '500'
  };
  
  const inputContainerStyle = {
    flex: 1,
    position: 'relative'
  };
  
  const inputStyle = {
    width: '100%',
    padding: '7px 5px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '80px',
    resize: 'vertical'
  };
  
  const suggestionsContainerStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '4px',
    maxHeight: '200px',
    overflowY: 'auto',
    zIndex: 1001,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  };
  
  const suggestionItemStyle = {
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    fontSize: '14px'
  };
  
  const suggestionTickerStyle = {
    fontWeight: 'bold',
    marginRight: '8px'
  };
  
  const suggestionCompanyStyle = {
    color: '#718096',
    fontSize: '13px'
  };
  
  const radioGroupStyle = {
    marginTop: '20px',
    marginBottom: '24px'
  };
  
  const radioLabelStyle = {
    marginRight: '20px',
    display: 'inline-flex',
    alignItems: 'center'
  };
  
  const buttonBarStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '16px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa'
  };
  
  const buttonStyle = {
    padding: '8px 20px',
    borderRadius: '4px',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px'
  };
  
  const createButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#4299e1',
    color: 'white',
    border: 'none',
    marginRight: '10px'
  };
  
  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'white',
    color: '#333',
    border: '1px solid #ddd'
  };

  const loadingButtonStyle = {
    ...createButtonStyle,
    backgroundColor: '#90cdf4',
    cursor: 'not-allowed'
  };

  const responseStyle = {
    padding: '10px',
    marginTop: '10px',
    borderRadius: '4px',
    backgroundColor: apiResponse?.success ? '#ebf8ff' : '#fed7d7',
    color: apiResponse?.success ? '#2b6cb0' : '#c53030',
    fontSize: '14px'
  };
  
  const successAlertStyle = {
    position: 'fixed',
    top: '20px',
    right: '20px',
    padding: '12px 20px',
    backgroundColor: '#c6f6d5',
    color: '#2f855a',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    zIndex: 1100,
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    transition: 'all 0.3s ease'
  };
  
  const alertIconStyle = {
    marginRight: '8px',
    width: '20px',
    height: '20px'
  };
  
  return (
    <div style={overlayStyle}>
      <div style={popupStyle}>
        <div style={headerStyle}>
          <div style={titleStyle}>
            <svg style={{ width: '18px', height: '18px', marginRight: '8px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
            </svg>
            Create Price Alert
          </div>
          <button 
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        
        <div style={contentStyle}>
          <div style={rowStyle}>
            <div style={labelStyle}>Ticker Code:</div>
            <div style={inputContainerStyle}>
              <input
                type="text"
                style={inputStyle}
                value={tickerCode}
                onChange={handleTickerInputChange}
                placeholder="Enter stock symbol (e.g. INFY)"
                autoComplete="off"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div style={suggestionsContainerStyle} ref={suggestionsRef}>
                  {suggestions.map((suggestion, index) => (
                    <div 
                      key={index} 
                      style={suggestionItemStyle}
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      <span style={suggestionTickerStyle}>{suggestion.ticker_code}</span>
                      <span style={suggestionCompanyStyle}>{suggestion.company_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div style={rowStyle}>
            <div style={labelStyle}>Price Type:</div>
            <div style={inputContainerStyle}>
              <select
                style={inputStyle}
                value={priceParam}
                onChange={(e) => setPriceParam(e.target.value)}
              >
                {priceParams.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div style={rowStyle}>
            <div style={labelStyle}>Condition:</div>
            <div style={inputContainerStyle}>
              <select
                style={inputStyle}
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
              >
                {conditions.map(cond => (
                  <option key={cond} value={cond}>{cond}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div style={rowStyle}>
            <div style={labelStyle}>Target Price:</div>
            <div style={inputContainerStyle}>
              <input
                type="number"
                style={inputStyle}
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                step="0.01"
                min="0"
                placeholder="Enter target price"
              />
            </div>
          </div>
          
          {/* Notes field */}
          <div style={rowStyle}>
            <div style={labelStyle}>Notes:</div>
            <div style={inputContainerStyle}>
              <textarea
                style={textareaStyle}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="(optional)"
              />
            </div>
          </div>
          
          <div style={radioGroupStyle}>
            <div style={{ marginBottom: '10px', color: '#555', fontWeight: '500' }}>Alert Options:</div>
            <label style={radioLabelStyle}>
              <input
                type="radio"
                name="alertType"
                checked={alertType === 'only'}
                onChange={() => setAlertType('only')}
                style={{ marginRight: '6px' }}
              />
              Only Alert
            </label>
          </div>
          
          
        </div>
        
        <div style={buttonBarStyle}>
          <button
            style={isLoading ? loadingButtonStyle : createButtonStyle}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Alert'}
          </button>
          <button
            style={cancelButtonStyle}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
      
      {alertSuccess && (
        <div style={successAlertStyle}>
          <svg style={alertIconStyle} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          Alert has been successfully created
        </div>
      )}
    </div>
  );
};

export default PriceAlertPopup;